import {Navigate, Outlet, Route, Routes} from 'react-router-dom';
import {lazy, Suspense} from 'react';
import AuthGate from './components/auth-gate';
import 'moment/locale/sk';
import 'moment/locale/de'
import 'moment/locale/cs'
import 'moment/locale/pl'
import 'moment/locale/it'
import 'moment/locale/ro'
import 'moment/locale/hu'
import CompanyReportPage from "./pages/company_report.tsx";
import ProductMetricReport from "./pages/product-report.tsx";
import {Companies} from "./pages/companies.tsx";
import {AddCompany} from "./pages/add-company.tsx";
import Unauthorized from "./pages/unauthorized.tsx";
import moment from "moment/moment";
import i18n from "i18next";
import FullPageLoader from "./components/full-page-loader.tsx";
import ErrorBoundary from "./components/error-boundary.tsx";
import NumericParamGate from "./components/numeric-param-gate.tsx";


// Lazy imports
const Products = lazy(() => import('./pages/products'));
const Reports = lazy(() => import('./pages/reports'));
const CustomReports = lazy(() => import('./pages/custom-reports'));
const CustomReportDetail = lazy(() => import('./pages/custom-report-detail'));
const CustomReportBuilder = lazy(() => import('./pages/custom-report-builder'));
const CustomReportRequest = lazy(() => import('./pages/custom-report-request'));
const CourseInfoTabs = lazy(() => import('./pages/course-info-tabs'));
const CourseInfoTabEdit = lazy(() => import('./pages/course-info-tab-edit'));
const CancellationReasons = lazy(() => import('./pages/cancellation-reasons'));
const Settings = lazy(() => import('./pages/settings'));
const Layout = lazy(() => import('./components/layouts/layout.tsx'));
const Verify = lazy(() => import('./pages/verify'));
const NotFound = lazy(() => import('./pages/not-found'));
const Dashboard = lazy(() => import("./pages/dashboard.tsx"))

export default function App() {

    // Set moment global locale - let moment use its native locales
    moment.locale(i18n.language || 'en');

    return (
        // OUTSIDE Suspense on purpose: every page below is `lazy()`, so a failed
        // chunk fetch rejects during the suspense resolution itself. A boundary
        // nested inside would never see it.
        <ErrorBoundary>
            <Suspense fallback={<FullPageLoader/>}>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/verify/:token" element={<Verify />} />
                    <Route path="/not-found" element={<NotFound />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* Private Routes */}
                    <Route element={<AuthGate><Layout><Outlet /></Layout></AuthGate>}>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/products" element={<Products />} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/reports/custom" element={<CustomReports />} />
                        <Route path="/reports/custom/new" element={<CustomReportBuilder />} />
                        <Route path="/reports/custom/request" element={<CustomReportRequest />} />
                        <Route path="/reports/custom/:id/edit" element={<NumericParamGate param="id"><CustomReportBuilder /></NumericParamGate>} />
                        <Route path="/reports/custom/:id" element={<NumericParamGate param="id"><CustomReportDetail /></NumericParamGate>} />
                        <Route path="/reports/product" element={<ProductMetricReport />} />
                        <Route path="/reports/product/:product" element={<ProductMetricReport />} />
                        <Route path="/reports/:id" element={<NumericParamGate param="id"><CompanyReportPage /></NumericParamGate>} />
                        <Route path="/companies" element={<Companies />} />
                        <Route path="/companies/add-company" element={<AddCompany />} />
                        <Route path="/course-info-tabs" element={<CourseInfoTabs />} />
                        <Route path="/course-info-tabs/new" element={<CourseInfoTabEdit />} />
                        <Route path="/course-info-tabs/:id" element={<NumericParamGate param="id"><CourseInfoTabEdit /></NumericParamGate>} />
                        <Route path="/cancellation-reasons" element={<CancellationReasons />} />
                        <Route path="/settings" element={<Settings />} />
                    </Route>

                    {/* Catch-all */}
                    <Route path="*" element={<Navigate to="/not-found" />} />
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );
}
